import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { toast } from 'sonner';
import { ElementsCaptured } from './ElementsCaptured';

export const ResultSection = ({ result, onRegenerate }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!result) return null;

  const { imageUrl, elementsCaptured } = result;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      const stamp = new Date().toISOString().split('T')[0];
      link.download = `linkedin-banner-${stamp}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Banner downloaded');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download banner. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section className="py-12 md:py-16 bg-muted/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Section Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 text-success mb-4">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Banner Generated</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
              Your LinkedIn banner is ready
            </h2>
            <p className="text-muted-foreground">
              Download it and paste it onto your LinkedIn profile.
            </p>
          </div>

          {/* Banner Preview */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-6"
          >
            <div className="bg-card border border-border rounded-2xl p-4 shadow-xl">
              {/* Single LinkedIn-context preview */}
              <div className="bg-muted rounded-lg overflow-hidden">
                <div className="aspect-[4/1] overflow-hidden bg-black">
                  <img
                    src={imageUrl}
                    alt="Generated LinkedIn banner"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                <div className="relative px-6 pb-4">
                  <div className="absolute -top-10 left-6 w-20 h-20 rounded-full border-4 border-card bg-muted-foreground/20 flex items-center justify-center">
                    <span className="text-xl text-muted-foreground/50">👤</span>
                  </div>
                  <div className="pt-14 pl-1">
                    <div className="h-3.5 w-40 bg-muted-foreground/20 rounded mb-2" />
                    <div className="h-3 w-28 bg-muted-foreground/10 rounded" />
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-muted-foreground mt-3">
                Preview of how it looks on a LinkedIn profile
              </p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-md mx-auto mb-8"
          >
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Downloading…</>
              ) : (
                <><Download className="w-5 h-5" /> Download banner</>
              )}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full mt-3"
              onClick={onRegenerate}
            >
              <RefreshCw className="w-5 h-5" />
              Try a different direction
            </Button>
          </motion.div>

          {/* Elements Captured transparency panel */}
          <ElementsCaptured elementsCaptured={elementsCaptured} />
        </motion.div>
      </div>
    </section>
  );
};

export default ResultSection;
